import { BarChart2, ChevronRight, Code2, FileText, Image, Mic } from 'lucide-react'
import type { WorkCategoryInfo } from '../../types/work'

interface WorkCategoryCardProps {
  category: WorkCategoryInfo
  selected: boolean
  onSelect: () => void
}

const ICON_MAP: Record<string, React.ReactNode> = {
  Mic: <Mic size={24} />,
  Image: <Image size={24} />,
  FileText: <FileText size={24} />,
  Code2: <Code2 size={24} />,
  BarChart2: <BarChart2 size={24} />,
}

export default function WorkCategoryCard({ category, selected, onSelect }: WorkCategoryCardProps) {
  return (
    <button
      onClick={onSelect}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        padding: '16px',
        borderRadius: '12px',
        border: selected
          ? '2px solid var(--primary, #6366f1)'
          : '1px solid var(--border-color, rgba(255,255,255,0.1))',
        background: selected
          ? 'rgba(99, 102, 241, 0.1)'
          : 'var(--card-bg, rgba(255,255,255,0.05))',
        cursor: 'pointer',
        textAlign: 'left',
        transition: 'all 0.15s ease',
        width: '100%',
        position: 'relative',
      }}
      onMouseEnter={(e) => {
        if (!selected) {
          (e.currentTarget as HTMLButtonElement).style.border = '1px solid var(--primary, #6366f1)'
          ;(e.currentTarget as HTMLButtonElement).style.background = 'rgba(99, 102, 241, 0.05)'
        }
      }}
      onMouseLeave={(e) => {
        if (!selected) {
          (e.currentTarget as HTMLButtonElement).style.border = '1px solid var(--border-color, rgba(255,255,255,0.1))'
          ;(e.currentTarget as HTMLButtonElement).style.background = 'var(--card-bg, rgba(255,255,255,0.05))'
        }
      }}
    >
      {/* Icon */}
      <div
        style={{
          width: '44px',
          height: '44px',
          borderRadius: '10px',
          background: selected
            ? 'rgba(99, 102, 241, 0.2)'
            : 'rgba(255,255,255,0.08)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: selected ? 'var(--primary, #6366f1)' : 'var(--text-secondary, #9ca3af)',
        }}
      >
        {ICON_MAP[category.icon] || <FileText size={24} />}
      </div>

      {/* Title + description */}
      <div style={{ flex: 1 }}>
        <div
          style={{
            fontSize: '14px',
            fontWeight: 600,
            color: 'var(--text-primary)',
            marginBottom: '4px',
          }}
        >
          {category.label}
        </div>
        <div
          style={{
            fontSize: '12px',
            color: 'var(--text-secondary, #9ca3af)',
            lineHeight: 1.4,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {category.description}
        </div>
      </div>

      {/* Subcategory count + arrow */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <span
          style={{
            fontSize: '11px',
            color: 'var(--text-muted, #6b7280)',
            background: 'rgba(255,255,255,0.06)',
            padding: '2px 8px',
            borderRadius: '20px',
          }}
        >
          {category.subcategories.length} service{category.subcategories.length !== 1 ? 's' : ''}
        </span>
        <ChevronRight
          size={14}
          style={{ color: selected ? 'var(--primary, #6366f1)' : 'var(--text-muted, #6b7280)' }}
        />
      </div>
    </button>
  )
}
